import { PROJECT_FILTER } from "types/project.types"

/**
 * Hierarchical react-query keys, one namespace per API domain.
 *
 * Invalidation cascades down the hierarchy: invalidating
 * `queryKeys.projects.root` invalidates every projects-related query
 * (lists, archived, folders, details, ...). Prefer invalidating the
 * narrowest key that covers the data a mutation changes.
 *
 * Extend with a new namespace per domain as migration proceeds
 * (users, documents, queries, reporting, ...).
 */
export const queryKeys = {
  projects: {
    root: ["projects"] as const,
    list: (filter: PROJECT_FILTER) => ["projects", "list", filter] as const,
    archived: ["projects", "archived"] as const,
    folders: ["projects", "folders"] as const,
    detail: (id: string) => ["projects", "detail", id] as const,
    members: (id: string) => ["projects", "members", id] as const,
    type: (id: string) => ["projects", "type", id] as const,
  },
  units: {
    root: ["units"] as const,
    users: ["units", "users"] as const,
  },
  documents: {
    root: ["documents"] as const,
    project: (projectId: string) => ["documents", projectId] as const,
    list: (projectId: string, filter: string, folder: string) =>
      ["documents", projectId, "list", filter, folder] as const,
  },
  automation: {
    root: ["automation"] as const,
    scripts: ["automation", "scripts"] as const,
    scriptMetadata: (name: string) =>
      ["automation", "scriptMetadata", name] as const,
    jobs: ["automation", "jobs"] as const,
    jobStatus: (jobId: string) => ["automation", "jobStatus", jobId] as const,
    // The unit's export scripts with their labels, for the CSV export's formats
    exportScripts: ["automation", "exportScripts"] as const,
  },
  savedQueries: {
    root: ["savedQueries"] as const,
    project: (projectId: string, owner: string) =>
      ["savedQueries", projectId, owner] as const,
    list: (projectId: string, owner: string) =>
      ["savedQueries", projectId, owner, "list"] as const,
    // The names alone (one per line from the server), to pick a query to trace.
    names: (projectId: string, owner: string) =>
      ["savedQueries", projectId, owner, "names"] as const,
    detail: (projectId: string, owner: string, name: string) =>
      ["savedQueries", projectId, owner, "detail", name] as const,
  },
  dashboards: {
    root: ["dashboards"] as const,
    list: (projectId: string) => ["dashboards", projectId, "list"] as const,
    detail: (projectId: string, name: string) =>
      ["dashboards", projectId, "detail", name] as const,
  },
  tabSpecs: {
    root: ["tabSpecs"] as const,
    list: (projectId: string, owner: string) =>
      ["tabSpecs", projectId, owner, "list"] as const,
    detail: (projectId: string, owner: string, name: string) =>
      ["tabSpecs", projectId, owner, "detail", name] as const,
  },
  // Everything read about one project's history sits under `project(projectId)`: a checkpoint
  // save, an import, an export or a tracker change invalidates that key and every read below it.
  // The owner is part of each key because the API calls send it.
  provenance: {
    root: ["provenance"] as const,
    project: (projectId: string) => ["provenance", projectId] as const,
    // The project history and its check (Timeline).
    audit: (projectId: string, owner: string) =>
      ["provenance", projectId, "audit", owner] as const,
    verify: (projectId: string, owner: string) =>
      ["provenance", projectId, "verify", owner] as const,
    // The version stamp and how the data differs from the questionnaire.
    projectVersion: (projectId: string, owner: string) =>
      ["provenance", projectId, "projectVersion", owner] as const,
    drift: (projectId: string, owner: string) =>
      ["provenance", projectId, "drift", owner] as const,
    // The projects this one was built from (merges, wave forks).
    ancestry: (projectId: string, owner: string) =>
      ["provenance", projectId, "ancestry", owner] as const,
    // The tracker this project belongs to, and another wave compared with this project.
    tracker: (projectId: string, owner: string) =>
      ["provenance", projectId, "tracker", owner] as const,
    compare: (projectId: string, owner: string, otherId: string, otherOwner: string) =>
      ["provenance", projectId, "compare", owner, otherId, otherOwner] as const,
    // Lineage: the questionnaire's fields, a saved query's graph, an export's captured graph.
    qnrMetadata: (projectId: string, owner: string) =>
      ["provenance", projectId, "qnrMetadata", owner] as const,
    lineage: (projectId: string, owner: string, query: string) =>
      ["provenance", projectId, "lineage", owner, query] as const,
    deliverableLineage: (projectId: string, owner: string, hash: string) =>
      ["provenance", projectId, "deliverableLineage", owner, hash] as const,
    // A live console session's lineage (not tied to the project's history).
    sessionLineage: (instanceId: string) =>
      ["provenance", "session", instanceId] as const,
  },
  dummyData: {
    root: ["dummy-data"] as const,
    project: (projectId: string) => ["dummy-data", projectId] as const,
  },
  templates: {
    root: ["templates"] as const,
    catalogue: ["templates", "catalogue"] as const,
  },
  datafileExists: {
    root: ["datafile-exists"] as const,
    project: (projectId: string) => ["datafile-exists", projectId] as const,
    file: (projectId: string, subtype: string) =>
      ["datafile-exists", projectId, subtype] as const,
  },
  dataChecks: {
    root: ["dataChecks"] as const,
    project: (projectId: string) => ["dataChecks", projectId] as const,
    // The routing-derived base check. Keyed by variant because an asked condition is a
    // statement about ONE flow, so two variants are two different answers, not one stale
    // one. `include_hidden` is deliberately NOT in the key: it moves a presentation flag
    // (`cleanable`) and no count, so the two calls share a cache entry and a re-run
    // overwrites it in place.
    baseCheck: (projectId: string, variant: string) =>
      ["dataChecks", projectId, "baseCheck", variant] as const,
  },
}
